import { useEffect, useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { authedFetch } from "../../lib/api-client";
import type { ApiEnvelope } from "@a2/core/schemas";
import { useT } from "../../lib/use-t";

type RecentSessionsResponse = {
  sessions: Array<{
    id: string;
    mode: string;
    startedAt: number;
    finishedAt: number | null;
    totalScore: number | null;
    maxScore: number | null;
    percentage: number | null;
    passed: boolean | null;
  }>;
};

type Point = { idx: number; pct: number; date: string; ts: number };

const CHART_HEIGHT = 96; // matches the original h-24 so layout doesn't shift between states

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const fmtDate = (d: Date) =>
  d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });

const fmtTime = (d: Date) =>
  d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

export function TrendStrip() {
  const t = useT();
  const [points, setPoints] = useState<Point[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/sessions/recent");
        const json = (await res.json()) as ApiEnvelope<RecentSessionsResponse>;
        if (!json.ok || cancelled) {
          if (!cancelled) setPoints([]);
          return;
        }
        // Why: when the user does several sessions on the same calendar
        // day, formatting every x-axis tick as "5 mei" makes the chart
        // unreadable — five identical labels collapse the points into a
        // visual blur. Detect the all-same-day case and switch to
        // HH:MM so each session is distinguishable.
        const dated = [...json.data.sessions]
          .filter((s) => s.percentage != null && s.finishedAt != null)
          .reverse()
          .map((s) => ({
            ts: (s.finishedAt ?? s.startedAt) * 1000,
            pct: Math.round((s.percentage ?? 0) * 100),
          }));
        const sameDay =
          dated.length > 1 &&
          new Set(dated.map((d) => dayKey(new Date(d.ts)))).size === 1;
        const data: Point[] = dated.map((d, i) => {
          const when = new Date(d.ts);
          return {
            idx: i,
            pct: d.pct,
            ts: d.ts,
            date: sameDay ? fmtTime(when) : fmtDate(when),
          };
        });
        setPoints(data);
      } catch {
        if (!cancelled) setPoints([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // De-framed: trend lives directly on the page surface — no card, no
  // border. The label row, the line, and the date row are typographic
  // siblings. Loading / empty / single-session states reserve the chart
  // height so the page doesn't shift on hydrate.
  const heading = (rightSlot: React.ReactNode) => (
    <div className="flex items-baseline justify-between gap-3">
      <h2
        id="trend-heading"
        className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
      >
        {t("history.trendStrip.heading")}
      </h2>
      {rightSlot}
    </div>
  );

  if (points === null) {
    // Silent height reservation — no copy, no shimmer text. Most users
    // see this for under 100 ms; nothing to read.
    return (
      <section aria-labelledby="trend-heading" className="space-y-2">
        {heading(null)}
        <div className="rounded bg-[var(--panel-soft)]/40 animate-pulse" style={{ height: CHART_HEIGHT }} aria-hidden />
      </section>
    );
  }

  if (points.length < 2) {
    const lastPct = points.length === 1 ? points[0]!.pct : null;
    const copy =
      points.length === 0
        ? t("history.trendStrip.emptyFirstSession")
        : t("history.trendStrip.emptySingleSession");

    return (
      <section aria-labelledby="trend-heading" className="space-y-2">
        {heading(
          lastPct !== null ? (
            <span className="font-mono-badge text-sm text-[var(--accent)]">{lastPct}%</span>
          ) : null,
        )}
        <p
          className="font-serif-italic text-base text-[var(--ink-muted)] flex items-center"
          style={{ minHeight: CHART_HEIGHT }}
        >
          {copy}
        </p>
      </section>
    );
  }

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const lastPct = last.pct;

  return (
    <section aria-labelledby="trend-heading" className="space-y-2">
      {heading(
        <span className="font-mono-badge text-sm text-[var(--accent)]">{lastPct}%</span>,
      )}
      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer>
          <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="date"
              tick={{ fill: "var(--ink-faint)", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide domain={[0, 100]} />
            <Tooltip
              contentStyle={{
                background: "var(--panel-soft)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(v) => [`${v}%`, "Score"]}
              labelStyle={{ color: "var(--ink-muted)" }}
            />
            <Line
              type="monotone"
              dataKey="pct"
              stroke="var(--accent)"
              strokeWidth={2.5}
              dot={{ r: 3, fill: "var(--accent)" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-[var(--ink-faint)] flex items-center justify-between">
        <span>{first.date}</span>
        <span>{t("history.trendStrip.lastSessionsFmt", { count: points.length })}</span>
        <span>{last.date}</span>
      </p>
    </section>
  );
}
